import { ComponentType, ReactNode, forwardRef } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { cn } from '~/utils/cn'
import { LoadingIcon, SubMenuIcon } from './icons'

export type MultiLevelMenuItem = {
  name: string
  label?: string
  onClick?: () => void | Promise<void>
  IconLeft?: ComponentType<React.ComponentProps<'svg'>>
  IconRight?: ComponentType<React.ComponentProps<'svg'>>
  buttonClassName?: string
  containerClassName?: string
  subMenuButtonContentClassName?: string
  subMenuContainerClassName?: string
  subMenu?: MultiLevelMenuItem[]
  hidden?: boolean
  disabled?: boolean
  loading?: boolean
}

type MultiLevelMenuItemContentProps = Pick<
  MultiLevelMenuItem,
  'label' | 'IconLeft' | 'IconRight'
>

function MultiLevelMenuItemContent({
  IconLeft,
  label,
  IconRight,
}: MultiLevelMenuItemContentProps) {
  return (
    <>
      {IconLeft && <IconLeft className="w-4 h-4" />}
      <span className="mr-5">{label}</span>
      {IconRight && <IconRight className="w-4 h-4 ml-auto" />}
    </>
  )
}

const commonClassNames = {
  menuItemContent:
    'flex justify-start gap-2 items-center w-full p-2 pl-4 text-sm leading-5 text-left rounded-lg',
  inactiveItem: 'text-gray-700 hover:bg-gray-100',
  activeItem: 'bg-gray-100 text-gray-900',
  disabled: 'text-gray-400 hover:bg-white',
}

type MultiLevelMenuItemComponentProps = {
  menuItem: MultiLevelMenuItem
  active: boolean
  close: () => void
}
const MultiLevelMenuItemComponent = forwardRef(
  ({ menuItem, active, close }: MultiLevelMenuItemComponentProps, ref) => {
    return menuItem.subMenu ? (
      <MultiLevelMenu
        menuItems={menuItem.subMenu}
        buttonClassName={menuItem.buttonClassName}
        menuContainerClassName={cn(
          '-translate-x-full ml-[-0.2rem] -translate-y-1/2',
          menuItem.subMenuContainerClassName
        )}
        renderButtonContent={(open) => (
          <div
            className={cn(
              commonClassNames.inactiveItem,
              {
                [commonClassNames.activeItem]: open,
              },
              commonClassNames.menuItemContent,
              menuItem.subMenuButtonContentClassName
            )}
          >
            <MultiLevelMenuItemContent
              label={menuItem.label}
              IconLeft={menuItem.IconLeft}
              IconRight={menuItem.loading ? LoadingIcon : SubMenuIcon}
            />
          </div>
        )}
      />
    ) : (
      <div className={menuItem?.containerClassName}>
        <button
          type="button"
          onClick={async () => {
            await menuItem.onClick?.()
            close()
          }}
          className={cn(
            commonClassNames.menuItemContent,
            {
              [commonClassNames.activeItem]: active,
              [commonClassNames.inactiveItem]: !active,
              [commonClassNames.disabled]: menuItem.disabled,
            },
            menuItem?.buttonClassName
          )}
          disabled={menuItem.disabled}
        >
          <MultiLevelMenuItemContent
            label={menuItem.label}
            IconLeft={menuItem.IconLeft}
            IconRight={menuItem.loading ? LoadingIcon : menuItem.IconRight}
          />
        </button>
      </div>
    )
  }
)

type MultiLevelMenuProps = {
  menuItems?: MultiLevelMenuItem[]
  buttonClassName?: string
  renderButtonContent: (open: boolean) => ReactNode
  menuContainerClassName?: string
  tooltipText?: string
}

export const MultiLevelMenu = ({
  menuItems = [],
  buttonClassName,
  renderButtonContent,
  menuContainerClassName,
}: MultiLevelMenuProps) => {
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <Menu>
        {({ open, close }) => (
          <>
            <Menu.Button
              className={cn(
                'rounded-md space-x-2 w-full flex justify-center items-center',
                buttonClassName
              )}
            >
              {renderButtonContent(open)}
            </Menu.Button>
            <Transition
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              className="relative z-10"
            >
              <Menu.Items
                className={cn(
                  'absolute w-max min-w-[10rem] px-2 py-1 bg-white border border-gray-200 rounded-md shadow-lg outline-none',
                  menuContainerClassName
                )}
              >
                {menuItems.map(
                  (menuItem) =>
                    !menuItem?.hidden && (
                      <Menu.Item key={menuItem.name}>
                        {({ active }) => (
                          <MultiLevelMenuItemComponent
                            menuItem={menuItem}
                            active={active}
                            close={close}
                          />
                        )}
                      </Menu.Item>
                    )
                )}
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  )
}
